import { Menu as _Menu } from '@base-ui/react/menu';
import { DirectionProvider } from '../utils/direction-provider';
import { MenuContext, MenuGroupContext } from './context';
import type { RootProps } from './types';
import { useIframeDismissalBridge } from '../utils/use-iframe-dismissal-bridge';

/**
 * Groups all parts of a menu.
 *
 * `Menu.Root` manages the open state and provides context for menu triggers,
 * popups, and items. Compose it with `Menu.Trigger` and `Menu.Popup` to build
 * a menu button, and with `Menu.SubmenuRoot` / `Menu.SubmenuTrigger` to build
 * nested menus.
 *
 * ```jsx
 * <Menu.Root>
 *   <Menu.Trigger>Open menu</Menu.Trigger>
 *   <Menu.Popup>
 *     <Menu.Item>
 *       <Menu.ItemLabel>Action</Menu.ItemLabel>
 *     </Menu.Item>
 *   </Menu.Popup>
 * </Menu.Root>
 * ```
 */
function Root( props: RootProps ) {
	const { onOpenChange } = props;

	const handleOpenChange: NonNullable< RootProps[ 'onOpenChange' ] > = (
		nextOpen,
		eventDetails
	) => {
		const trigger = eventDetails.trigger;
		const TriggerElement = trigger?.ownerDocument.defaultView?.HTMLElement;
		const EventTargetElement = trigger?.ownerDocument.defaultView?.Element;
		const activeElementBeforeOpenChange =
			trigger?.ownerDocument.activeElement;
		const eventTarget = eventDetails.event.target;
		const menuElement =
			EventTargetElement && eventTarget instanceof EventTargetElement
				? eventTarget.closest( '[role="menu"]' )
				: null;

		onOpenChange?.( nextOpen, eventDetails );

		if (
			! nextOpen &&
			eventDetails.reason === 'item-press' &&
			! eventDetails.isCanceled &&
			TriggerElement &&
			trigger instanceof TriggerElement &&
			menuElement?.contains( activeElementBeforeOpenChange ?? null ) &&
			trigger.ownerDocument.activeElement ===
				activeElementBeforeOpenChange
		) {
			/*
			 * Move focus before the item's click handler can mount another overlay.
			 * This lets that overlay capture the menu trigger, rather than the menu
			 * item that is about to be unmounted, as its focus return target. Do not
			 * override focus moved explicitly by the consumer's onOpenChange handler.
			 */
			trigger.focus();
		}
	};
	const iframeDismissalProps = useIframeDismissalBridge<
		_Menu.Root.Actions,
		_Menu.Root.ChangeEventDetails
	>( {
		actionsRef: props.actionsRef,
		defaultOpen: props.defaultOpen,
		modal: props.modal,
		onOpenChange: handleOpenChange,
		open: props.open,
	} );

	return (
		<DirectionProvider>
			<MenuContext.Provider value={ { isSubmenu: false } }>
				<MenuGroupContext.Provider value={ null }>
					<_Menu.Root { ...props } { ...iframeDismissalProps } />
				</MenuGroupContext.Provider>
			</MenuContext.Provider>
		</DirectionProvider>
	);
}

export { Root };
